// Reine Funktionen des Aktenschranks (Handoff Dokumente §6.2): Pfad-Zähler, Breadcrumb, Quellen, Dateitypen.

import type { BaumKnoten } from './aktenschrankApi';

/** Anzahl Dateien je Ordnerpfad inkl. aller Unterordner (aus dokumente.ablage_pfad). */
export function dateienJePfad(pfade: readonly (string | null)[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const p of pfade) {
    const teile = (p ?? '').split('/').map(s => s.trim()).filter(Boolean);
    let akt = '';
    for (const t of teile) {
      akt = akt ? `${akt}/${t}` : t;
      m.set(akt, (m.get(akt) ?? 0) + 1);
    }
  }
  return m;
}

/** Dateien direkt in einem Ordner (ohne Unterordner). */
export function dateienDirekt(pfade: readonly (string | null)[], pfad: string): number {
  return pfade.filter(p => (p ?? '').trim() === pfad).length;
}

/** Knoten je Drive-ID und je Pfad (für Breadcrumb und Sprünge). */
export function baumIndex(wurzeln: readonly BaumKnoten[]): { nachId: Map<string, BaumKnoten>; nachPfad: Map<string, BaumKnoten> } {
  const nachId = new Map<string, BaumKnoten>();
  const nachPfad = new Map<string, BaumKnoten>();
  const lauf = (liste: readonly BaumKnoten[]) => { for (const k of liste) { nachId.set(k.gdriveId, k); nachPfad.set(k.pfad, k); lauf(k.kinder); } };
  lauf(wurzeln);
  return { nachId, nachPfad };
}

export interface Segment { id: string; name: string; pfad: string }

/** Breadcrumb vom Objekt-Ordner bis zum Knoten (Wurzel = Pfad ''). */
export function pfadSegmente(wurzelId: string, wurzelName: string, knoten: BaumKnoten | null, nachPfad: ReadonlyMap<string, BaumKnoten>): Segment[] {
  const seg: Segment[] = [{ id: wurzelId, name: wurzelName, pfad: '' }];
  if (!knoten) return seg;
  let akt = '';
  for (const teil of knoten.pfad.split('/')) {
    akt = akt ? `${akt}/${teil}` : teil;
    const k = nachPfad.get(akt);
    if (k) seg.push({ id: k.gdriveId, name: k.name, pfad: k.pfad });
  }
  return seg;
}

/** Alle Elternpfade (zum Aufklappen bis zu einem Ordner). */
export function elternPfade(pfad: string): string[] {
  const teile = pfad.split('/').filter(Boolean);
  return teile.map((_, i) => teile.slice(0, i + 1).join('/'));
}

/** Herkunft einer Datei für die Zeile (Upload / E-Mail / Signatur …). */
export function quelleLabel(quelle: string | null | undefined, kategorie?: string | null): string {
  switch ((quelle ?? '').toLowerCase()) {
    case 'email': return 'E-Mail';
    case 'manuell': return (kategorie ?? '').toLowerCase().includes('signiert') ? 'Signatur' : 'Upload';
    case 'uebergabe': return 'Übergabeprotokoll';
    case 'api': return 'Schnittstelle';
    case '': return 'Google Drive';
    default: return quelle?.startsWith('onedrive') ? 'OneDrive' : 'Google Drive';
  }
}

export type DateiArt = 'pdf' | 'bild' | 'word' | 'excel' | 'sonst';
export function dateiArt(name: string, mime?: string | null): DateiArt {
  const n = name.toLowerCase();
  const m = (mime ?? '').toLowerCase();
  if (n.endsWith('.pdf') || m === 'application/pdf') return 'pdf';
  if (/\.(png|jpe?g|gif|webp|heic|tiff?)$/.test(n) || m.startsWith('image/')) return 'bild';
  if (/\.(docx?|odt|rtf)$/.test(n) || m.includes('word')) return 'word';
  if (/\.(xlsx?|ods|csv)$/.test(n) || m.includes('sheet') || m.includes('excel')) return 'excel';
  return 'sonst';
}

/** Name ohne Endung (Umbenennen) und Endung wieder anhängen. */
export function trenneEndung(name: string): { basis: string; endung: string } {
  const i = name.lastIndexOf('.');
  return i > 0 && i >= name.length - 6 ? { basis: name.slice(0, i), endung: name.slice(i) } : { basis: name, endung: '' };
}
export function mitEndung(basis: string, endung: string): string {
  const b = basis.trim();
  return endung && !b.toLowerCase().endsWith(endung.toLowerCase()) ? `${b}${endung}` : b;
}

/** Ausschnitt um den ersten Treffer (Inhaltssuche), mit „…“ an abgeschnittenen Enden. */
export function textAusschnitt(text: string | null | undefined, anfrage: string, laenge = 120): string {
  const t = (text ?? '').replace(/\s+/g, ' ').trim();
  if (!t) return '';
  const wort = anfrage.trim().toLowerCase().split(/\s+/).find(w => w.length >= 2) ?? '';
  const i = wort ? t.toLowerCase().indexOf(wort) : -1;
  if (i < 0) return t.length > laenge ? `${t.slice(0, laenge).trimEnd()} …` : t;
  const start = Math.max(0, i - Math.floor(laenge / 3));
  const ende = Math.min(t.length, start + laenge);
  return `${start > 0 ? '… ' : ''}${t.slice(start, ende).trim()}${ende < t.length ? ' …' : ''}`;
}

/** Zeile unter einem Unterordner: „3 Dateien · 2 Ordner“ (nur bekannte Werte). */
export function ordnerZeile(dateien: number | null, ordner: number | null): string {
  const teile: string[] = [];
  if (dateien !== null) teile.push(`${dateien} ${dateien === 1 ? 'Datei' : 'Dateien'}`);
  if (ordner) teile.push(`${ordner} Ordner`);
  return teile.join(' · ') || 'Ordner';
}

/** Suchwörter für PostgREST-Filter: Steuerzeichen der Filtersyntax entfernt, mindestens 2 Zeichen, höchstens 5 Wörter. */
export function suchWoerter(q: string): string[] {
  return q.replace(/[,()*%\\:"']/g, ' ').split(/\s+/).map(w => w.trim()).filter(w => w.length >= 2).slice(0, 5);
}

/** Ordnerbaum flach mit Tiefe (Auswahllisten für Zielordner). */
export function flacheOrdner(wurzeln: readonly BaumKnoten[], tiefe = 0): { pfad: string; name: string; tiefe: number }[] {
  return wurzeln.flatMap(k => [{ pfad: k.pfad, name: k.name, tiefe }, ...flacheOrdner(k.kinder, tiefe + 1)]);
}

// ── #558: Suchbereich ────────────────────────────────────────────────────────
export type SuchBereich = 'ordner' | 'objekt' | 'alle';

/** Voreinstellung: im Unterordner → dieser Ordner, auf Objektebene → Objekt, sonst überall. */
export function standardSuchBereich(objektId: string | null, ordnerId: string | null, wurzelId: string | null): SuchBereich {
  if (!objektId || !wurzelId) return 'alle';
  return ordnerId && ordnerId !== wurzelId ? 'ordner' : 'objekt';
}

/** Ordner-IDs, in denen gesucht wird: Startordner samt aller Unterordner aus dem Baum. */
export function suchOrdnerIds(startId: string, wurzelId: string, baum: readonly BaumKnoten[], nachId: ReadonlyMap<string, BaumKnoten>): string[] {
  const ids: string[] = [startId];
  const lauf = (l: readonly BaumKnoten[]) => l.forEach(k => { ids.push(k.gdriveId); lauf(k.kinder); });
  lauf(startId === wurzelId ? baum : (nachId.get(startId)?.kinder ?? []));
  return [...new Set(ids)];
}
